import { adminApi, ensureArray } from '../services/api';
import { useState, useEffect } from 'react';

interface HmoKey {
  id: string;
  hmoName: string;
  contactEmail: string;
  keyPrefix: string;
  active: boolean;
  rateLimit: number;
  expiresAt: string;
  createdAt: string;
}

export function HmoKeysPage() {
  const [keys, setKeys] = useState<HmoKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ hmoName: '', contactEmail: '', rateLimit: '1000' });

  const load = () => {
    setLoading(true);
    adminApi<HmoKey[]>('/admin/hmo-keys')
      .then((d) => setKeys(ensureArray(d)))
      .catch(() => setKeys([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setNewKey(null);
    setError('');
    try {
      const data = await adminApi<{ apiKey?: string }>('/admin/hmo-keys', {
        method: 'POST',
        body: JSON.stringify({
          hmoName: form.hmoName,
          contactEmail: form.contactEmail,
          rateLimit: parseInt(form.rateLimit, 10),
        }),
      });
      if (data.apiKey) {
        setNewKey(data.apiKey);
        setForm({ hmoName: '', contactEmail: '', rateLimit: '1000' });
        load();
      }
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'Could not create that key.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">HMO API Keys</h1>
        <p className="mt-0.5 text-sm text-gray-500">
          Issue partner API keys for HMO session verification and claims
        </p>
      </div>

      <form
        onSubmit={create}
        className="mb-8 max-w-lg rounded-xl border border-gray-200 bg-white p-5 shadow-sm"
      >
        <h2 className="mb-4 font-semibold text-gray-900">Create Key</h2>
        <div className="space-y-3">
          <input
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            placeholder="HMO name"
            value={form.hmoName}
            onChange={(e) => setForm((f) => ({ ...f, hmoName: e.target.value }))}
            required
          />
          <input
            type="email"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            placeholder="Contact email"
            value={form.contactEmail}
            onChange={(e) => setForm((f) => ({ ...f, contactEmail: e.target.value }))}
            required
          />
          <input
            type="number"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            placeholder="Rate limit / hour"
            value={form.rateLimit}
            onChange={(e) => setForm((f) => ({ ...f, rateLimit: e.target.value }))}
          />
          <button
            type="submit"
            disabled={creating}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {creating ? 'Creating…' : 'Generate API Key'}
          </button>
        </div>
        {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}
        {newKey && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <p className="font-medium">Copy this key now — it will not be shown again:</p>
            <code className="mt-1 block break-all font-mono text-xs">{newKey}</code>
          </div>
        )}
      </form>

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        {loading ? (
          <div className="p-8 text-center text-gray-400">Loading…</div>
        ) : keys.length === 0 ? (
          <div className="p-12 text-center text-gray-400">No HMO keys yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase text-gray-500">
                <th className="px-4 py-3">HMO</th>
                <th className="px-4 py-3">Prefix</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Rate Limit</th>
                <th className="px-4 py-3">Expires</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {keys.map((k) => (
                <tr key={k.id}>
                  <td className="px-4 py-3 font-medium">{k.hmoName}</td>
                  <td className="px-4 py-3 font-mono text-xs">{k.keyPrefix}…</td>
                  <td className="px-4 py-3 text-gray-500">{k.contactEmail}</td>
                  <td className="px-4 py-3">{k.rateLimit}/hr</td>
                  <td className="px-4 py-3 text-gray-500">
                    {new Date(k.expiresAt).toLocaleDateString('en-NG')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
